import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAuction } from '../../context/AuctionContext';
import { collection, getDocs, doc, setDoc, getDoc, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { seasonPlayersCollection } from '../../services/players';
import { db } from '../../firebase';
import {
    Box,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip,
    FormControlLabel,
    Switch,
    CircularProgress,
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import PsychologyIcon from '@mui/icons-material/Psychology';
import TacticsModal from '../../components/TacticsModal';
import ParticipantTeamModal from '../../components/ParticipantTeamModal';
import { page } from '../../theme/commonStyles';

const tactics = [
    { id: 'totaalvoetbal', name: 'Nederlands Totaal voetbal' },
    { id: 'barcelona', name: 'Dominant Barcelona voetbal' },
    { id: 'mourinho', name: 'Mourinho’s Bus', },
    { id: 'catenaccio', name: 'Italiaans Catenaccio voetbal' },
];

function Schedule() {
    const { user } = useAuth();
    const { auctionId } = useAuction();
    const [scheduleData, setScheduleData] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selections, setSelections] = useState({});
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showOnlyMyMatches, setShowOnlyMyMatches] = useState(() => {
        const saved = localStorage.getItem(`showOnlyMyMatches_${auctionId}`);
        return saved ? JSON.parse(saved) : false;
    });
    const [editingMatch, setEditingMatch] = useState(null);
    const [teamPlayers, setTeamPlayers] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [auction, setAuction] = useState(null); // New state for auction
    const [modalRoundId, setModalRoundId] = useState(null); // New state for roundId for modal
    const [modalMatchId, setModalMatchId] = useState(null);
    const [modalRoundStatus, setModalRoundStatus] = useState(null);
    const [matchScores, setMatchScores] = useState({}); // State for calculated match scores
    const [intermediateRoundScores, setIntermediateRoundScores] = useState({}); // State for intermediate round scores per participant
    const [allSelections, setAllSelections] = useState({});
    const [showTacticsAndCaptains, setShowTacticsAndCaptains] = useState(() => {
        const saved = localStorage.getItem(`showTacticsAndCaptains_${auctionId}`);
        return saved ? JSON.parse(saved) : false;
    }); // State for showing tactics and captains
    const [playerDetails, setPlayerDetails] = useState(new Map()); // State for captain details
    const [infoDialogOpen, setInfoDialogOpen] = useState(false);
    const [infoDialogMessage, setInfoDialogMessage] = useState('');
    const [liveRankingIntermediateRound, setLiveRankingIntermediateRound] = useState(null);
    const [liveRankingFinaleronde, setLiveRankingFinaleronde] = useState(null);
    const [finalRoundParticipants, setFinalRoundParticipants] = useState(null);
    const [editingIntermediateRound, setEditingIntermediateRound] = useState(null); // For intermediate round captain selection
    const [isIntermediateCaptainModalOpen, setIsIntermediateCaptainModalOpen] = useState(false);

    useEffect(() => {
        if (!auctionId || !user) {
            setLoading(false);
            return;
        }

        // Fetch auction data to get seasonId
        const unsubAuction = onSnapshot(doc(db, 'auctions', auctionId), (docSnap) => {
            if (docSnap.exists()) {
                setAuction(docSnap.data());
            }
        }, (err) => {
            console.error("Error fetching auction data:", err);
            setError("Kon veilingdata niet ophalen.");
        });

        const scheduleRef = collection(db, 'auctions', auctionId, 'schedule');
        const unsubSchedule = onSnapshot(scheduleRef, (snapshot) => {
            const fetchedSchedule = {};
            snapshot.forEach(doc => {
                fetchedSchedule[doc.id] = doc.data(); // Store full document data including roundId
            });
            setScheduleData(fetchedSchedule);
            setLoading(false);
        }, (err) => {
            setError('Fout bij het ophalen van het speelschema.');
            setLoading(false);
        });

        const selectionRef = doc(db, 'auctions', auctionId, 'matchSelections', user.uid);
        const unsubSelection = onSnapshot(selectionRef, (docSnap) => {
            if (docSnap.exists()) {
                setSelections(docSnap.data().selections || {});
            }
        });

        const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc'));
        const unsubTransfers = onSnapshot(transfersQuery, async (transfersSnap) => {
            const myPlayersMap = new Map();

            transfersSnap.docs.forEach(doc => {
                const transfer = doc.data();
                if (transfer.to === user.uid) {
                    myPlayersMap.set(transfer.playerId, { id: transfer.playerId, name: transfer.playerName });
                }
                if (transfer.from === user.uid && transfer.to !== user.uid) {
                    myPlayersMap.delete(transfer.playerId);
                }
            });

            const playerIds = Array.from(myPlayersMap.keys());

            if (playerIds.length === 0) {
                setTeamPlayers([]);
                return;
            }

            // Spelers staan per seizoen; zonder de veiling weten we niet welk.
            if (!auction?.season) {
                return;
            }

            try {
                const playerIdsAsNumbers = playerIds.map(id => Number(id));
                const playersQuery = query(seasonPlayersCollection(auction.season), where('id', 'in', playerIdsAsNumbers));
                const playersSnap = await getDocs(playersQuery);
                const fetchedPlayers = playersSnap.docs.map(doc => doc.data());

                const positionMap = {
                    'Goalkeeper': 'Keeper',
                    'Defender': 'Verdediger',
                    'Midfielder': 'Middenvelder',
                    'Attacker': 'Aanvaller',
                };
                const translatedPlayers = fetchedPlayers
                    .filter(p => p.position !== 'Coach') // Coaches can't be captains
                    .map(p => ({ ...p, position: positionMap[p.position] || p.position }));

                setTeamPlayers(translatedPlayers);

            } catch (err) {
                console.error("Fout bij het ophalen van spelerdetails na transfer update:", err);
                setError('Kon spelerdetails niet bijwerken.');
            }
        }, (err) => {
            console.error("Error listening to transfers:", err);
            setError('Kon transfers niet live bijwerken.');
        });

        const participantsQuery = query(collection(db, 'auctions', auctionId, 'participants'));
        const unsubParticipants = onSnapshot(participantsQuery, (snapshot) => {
            const fetchedParticipants = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setParticipants(fetchedParticipants);
        });

        const selectionsQuery = query(collection(db, 'auctions', auctionId, 'matchSelections'));
        const unsubAllSelections = onSnapshot(selectionsQuery, (snapshot) => {
            const selectionsData = {};
            snapshot.docs.forEach(doc => {
                selectionsData[doc.id] = doc.data().selections;
            });
            setAllSelections(selectionsData);
        });

        // Listener voor liveRankingIntermediateRound
        const liveRankingIntermediateRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingIntermediateRound');
        const unsubLiveRankingIntermediate = onSnapshot(liveRankingIntermediateRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingIntermediateRound(docSnap.data());
            } else {
                setLiveRankingIntermediateRound(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingIntermediateRound:", err);
        });

        const liveRankingFinalRef = doc(db, 'auctions', auctionId, 'ranking', 'liveRankingFinaleronde');
        const unsubLiveRankingFinal = onSnapshot(liveRankingFinalRef, (docSnap) => {
            if (docSnap.exists()) {
                setLiveRankingFinaleronde(docSnap.data());
            } else {
                setLiveRankingFinaleronde(null);
            }
        }, (err) => {
            console.error("Error fetching liveRankingFinaleronde:", err);
        });

        const finalRoundParticipantsRef = doc(db, 'auctions', auctionId, 'ranking', 'finalRoundParticipants');
        const unsubFinalRoundParticipants = onSnapshot(finalRoundParticipantsRef, (docSnap) => {
            if (docSnap.exists()) {
                setFinalRoundParticipants(docSnap.data());
            } else {
                setFinalRoundParticipants(null);
            }
        }, (err) => {
            console.error("Error fetching finalRoundParticipants:", err);
        });

        return () => {
            unsubAuction();
            unsubSchedule();
            unsubSelection();
            unsubTransfers();
            unsubParticipants();
            unsubAllSelections();
            unsubLiveRankingIntermediate();
            unsubLiveRankingFinal();
            unsubFinalRoundParticipants();
        };
    }, [auctionId, user, auction?.season]);

    const participantsByName = useMemo(() => {
        const map = new Map();
        participants.forEach(p => {
            map.set(p.name, p);
        });
        return map;
    }, [participants]);

    const participantNameToUidMap = useMemo(() => {
        const map = new Map();
        participants.forEach(p => {
            map.set(p.name, p.id);
        });
        return map;
    }, [participants]);

    const currentUserParticipantName = useMemo(() => {
        if (!user || !participants.length) return null;
        const participant = participants.find(p => p.id === user.uid);
        return participant?.name;
    }, [user, participants]);

    // Helper to check if a round is a knockout round (tussenronde)
    const isIntermediateKnockoutRound = (roundData) => {
        return roundData.type === 'knockout' && roundData.stageName === 'Knockout Round Play-offs';
    };

    const isFinalKnockoutRound = (roundData) => {
        if (roundData.type !== 'knockout') return false;
        const isFinalStage = ['Quarter-finals', 'Semi-finals', 'Final'].includes(roundData.stageName);
        // Zorg dat alleen entries MET leg-velden worden meegenomen
        // (oude entries zonder legs worden genegeerd)
        return isFinalStage && roundData.leg;
    };

    // Helper to check if a round is any knockout round
    const isAnyKnockoutRound = (roundData) => {
        return roundData.type === 'knockout';
    };

    const sortedSchedule = useMemo(() => {
        if (!scheduleData) return [];
        return Object.entries(scheduleData)
            .filter(([key, roundData]) => !isAnyKnockoutRound(roundData) && !key.startsWith('intermediateRound')) // Filter out all knockout rounds
            .sort(([keyA], [keyB]) => {
                const numA = parseInt(keyA.replace('round', ''), 10);
                const numB = parseInt(keyB.replace('round', ''), 10);
                return numA - numB;
            });
    }, [scheduleData]);

    // Separate knockout rounds from the schedule (tussenronde)
    const intermediateRounds = useMemo(() => {
        if (!scheduleData) return [];
        return Object.entries(scheduleData)
            .filter(([key, roundData]) => isIntermediateKnockoutRound(roundData) || key.startsWith('intermediateRound'))
            .sort(([keyA, dataA], [keyB, dataB]) => {
                // Sort by leg number
                const legA = dataA.leg ? parseInt(dataA.leg.split('/')[0], 10) : parseInt(keyA.replace('intermediateRound', ''), 10);
                const legB = dataB.leg ? parseInt(dataB.leg.split('/')[0], 10) : parseInt(keyB.replace('intermediateRound', ''), 10);
                return legA - legB;
            });
    }, [scheduleData]);

    // Knock-out achtste finale rondes
    const knockoutAchtsteRounds = useMemo(() => {
        if (!scheduleData) return [];
        return Object.entries(scheduleData)
            .filter(([, roundData]) => roundData.type === 'knockout' && roundData.stageName === '8th Finals')
            .sort(([, a], [, b]) => (a.leg || '').localeCompare(b.leg || ''));
    }, [scheduleData]);

    const finalRounds = useMemo(() => {
        if (!scheduleData) return [];
        const stageOrder = {
            'Quarter-finals': 1,
            'Semi-finals': 2,
            'Final': 3,
        };
        return Object.entries(scheduleData)
            .filter(([, roundData]) => isFinalKnockoutRound(roundData))
            .sort(([, a], [, b]) => {
                const stageDiff = (stageOrder[a.stageName] || 99) - (stageOrder[b.stageName] || 99);
                if (stageDiff !== 0) return stageDiff;
                const legA = parseInt((a.leg || '1/1').split('/')[0], 10) || 1;
                const legB = parseInt((b.leg || '1/1').split('/')[0], 10) || 1;
                return legA - legB;
            });
    }, [scheduleData]);

    // Get ALL participants for the tussenronde section (so top-15 can also select a captain)
    const intermediateParticipants = useMemo(() => {
        if (!liveRankingIntermediateRound) return [];
        // Include all participants, not just the 16+ from liveRankingIntermediateRound
        const allParticipants = participants.map(p => ({ uid: p.id, name: p.name }));
        
        // Calculate total score across all intermediate rounds per participant
        const getTotalScore = (uid) => {
            let total = 0;
            Object.values(intermediateRoundScores).forEach(roundScores => {
                total += roundScores[uid] || 0;
            });
            return total;
        };
        
        // Sort by total score (highest first), then alphabetically by name
        return allParticipants.sort((a, b) => {
            const scoreA = getTotalScore(a.uid);
            const scoreB = getTotalScore(b.uid);
            if (scoreB !== scoreA) return scoreB - scoreA;
            return (a.name || '').localeCompare(b.name || '');
        });
    }, [liveRankingIntermediateRound, participants, intermediateRoundScores]);

    const finalRoundParticipantsList = useMemo(() => {
        const finalists = finalRoundParticipants?.participants || [];
        if (finalists.length > 0) {
            const byUid = new Map(participants.map(p => [p.id, p]));
            return finalists.map(f => ({
                uid: f.uid,
                name: byUid.get(f.uid)?.name || f.name || 'Onbekend'
            }));
        }

        if (liveRankingFinaleronde?.ranking?.length) {
            return liveRankingFinaleronde.ranking.map(r => ({ uid: r.uid, name: r.name }));
        }

        return [];
    }, [finalRoundParticipants, liveRankingFinaleronde, participants]);

    const firstNonClosedRoundIndex = useMemo(() => {
        if (sortedSchedule.length === 0) return 0;
        const firstIndex = sortedSchedule.findIndex(([, roundData]) => roundData.status !== 'closed');
        return firstIndex === -1 ? 0 : firstIndex;
    }, [sortedSchedule]);

    // Fetch player details for all captains when selections change
    useEffect(() => {
        if (!allSelections || Object.keys(allSelections).length === 0) return;

        const fetchCaptainDetails = async () => {
            const captainIds = new Set();
            Object.values(allSelections).forEach(userSelections => {
                if (userSelections) {
                    Object.values(userSelections).forEach(selection => {
                        // Add captain ID to set if it exists and we don't have details for it yet
                        if (selection.captain && !playerDetails.has(String(selection.captain))) {
                            captainIds.add(Number(selection.captain));
                        }
                    });
                }
            });

            if (captainIds.size === 0) return;
            if (!auction?.season) return;

            const playerIdsToFetch = Array.from(captainIds);

            // Firestore 'in' query is limited to 30 elements, so we chunk the requests
            const chunks = [];
            for (let i = 0; i < playerIdsToFetch.length; i += 30) {
                chunks.push(playerIdsToFetch.slice(i, i + 30));
            }

            try {
                const playerPromises = chunks.map(chunk =>
                    getDocs(query(seasonPlayersCollection(auction.season), where('id', 'in', chunk)))
                );
                const playerSnapshots = await Promise.all(playerPromises);

                setPlayerDetails(prevDetails => {
                    const newDetails = new Map(prevDetails);
                    playerSnapshots.forEach(snapshot => snapshot.forEach(doc => newDetails.set(String(doc.data().id), doc.data())));
                    return newDetails;
                });
            } catch (err) {
                console.error("Failed to fetch captain details:", err);
            }
        };

        fetchCaptainDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSelections]);

    // Calculate all match scores when data changes
    useEffect(() => {
        const calculateAllScores = async () => {
            if (!scheduleData || Object.keys(scheduleData).length === 0 || !auction?.season || participants.length === 0) {
                setMatchScores({});
                return;
            }

            // 1. Fetch all data in bulk
            const transfersQuery = query(collection(db, 'auctions', auctionId, 'auctionTransfers'), orderBy('timestamp', 'asc'));
            const transfersSnap = await getDocs(transfersQuery);

            const allPlayerScores = new Map(); // Map<roundId, Map<playerId, score[]>>
            const scorePromises = Object.values(scheduleData)
                .filter(round => round.status !== 'closed' && round.roundId)
                .map(round => {
                    const scoresQuery = collection(db, `playerScores/${auction.season}/rounds/${round.roundId}/scores`);
                    return getDocs(scoresQuery).then(snapshot => {
                        const scoresByPlayer = new Map();
                        snapshot.forEach(doc => {
                            const scoreData = doc.data();
                            const playerId = String(scoreData.playerId);
                            if (!scoresByPlayer.has(playerId)) {
                                scoresByPlayer.set(playerId, []);
                            }
                            scoresByPlayer.get(playerId).push(scoreData);
                        });
                        allPlayerScores.set(round.roundId, scoresByPlayer);
                    });
                });

            const allRoundRankings = new Map(); // Map<roundKey, results[]>
            const rankingPromises = Object.entries(scheduleData)
                .filter(([, round]) => round.status === 'closed')
                .map(([roundKey]) => {
                    const roundRankingRef = doc(db, 'auctions', auctionId, 'roundRankings', roundKey);
                    return getDoc(roundRankingRef).then(roundRankingSnap => {
                        if (roundRankingSnap.exists()) {
                            allRoundRankings.set(roundKey, roundRankingSnap.data().results || []);
                        }
                    });
                });

            await Promise.all([...scorePromises, ...rankingPromises]);

            // 2. Pre-calculate team compositions
            const teamCompositions = new Map(); // Map<participantId, Set<playerId>>
            participants.forEach(p => teamCompositions.set(p.id, new Set()));
            transfersSnap.docs.forEach(doc => {
                const transfer = doc.data();
                if (teamCompositions.has(transfer.to)) teamCompositions.get(transfer.to).add(String(transfer.playerId));
                if (teamCompositions.has(transfer.from)) teamCompositions.get(transfer.from).delete(String(transfer.playerId));
            });

            // 3. Loop through schedule and calculate scores
            const newScores = {};
            for (const [roundKey, roundData] of Object.entries(scheduleData)) {
                if (!roundData.matches) continue;

                for (const [matchIndex, match] of roundData.matches.entries()) {
                    const matchId = `${roundKey}-${matchIndex}`;
                    const homeParticipant = participantsByName.get(match.home);
                    const awayParticipant = participantsByName.get(match.away);

                    if (!homeParticipant || !awayParticipant) {
                        newScores[matchId] = { homeScore: 0, awayScore: 0 };
                        continue;
                    }

                    let homeScore = 0;
                    let awayScore = 0;

                    if (roundData.status === 'closed') {
                        const roundRanking = allRoundRankings.get(roundKey);
                        if (roundRanking) {
                            const homeResult = roundRanking.find(r => r.uid === homeParticipant.id);
                            const awayResult = roundRanking.find(r => r.uid === awayParticipant.id);
                            if (homeResult) {
                                homeScore = (homeResult.playerPoints || 0) + (homeResult.tacticPoints || 0) + (homeResult.tacticCounter || 0);
                            }
                            if (awayResult) {
                                awayScore = (awayResult.playerPoints || 0) + (awayResult.tacticPoints || 0) + (awayResult.tacticCounter || 0);
                            }
                        }
                    } else if (roundData.roundId) {
                        const playerScoresForRound = allPlayerScores.get(roundData.roundId);
                        if (playerScoresForRound) {
                            const homeTeamPlayers = Array.from(teamCompositions.get(homeParticipant.id) || []);
                            const awayTeamPlayers = Array.from(teamCompositions.get(awayParticipant.id) || []);

                            const homeSelection = allSelections[homeParticipant.id]?.[matchId] || {};
                            const awaySelection = allSelections[awayParticipant.id]?.[matchId] || {};

                            homeTeamPlayers.forEach(playerId => {
                                const scoresArray = playerScoresForRound.get(playerId) || [];
                                const isCaptain = playerId === String(homeSelection.captain);

                                if (isCaptain) {
                                    let totalPoints = 0;
                                    let totalBonusPoints = 0;
                                    scoresArray.forEach(score => {
                                        const bonusPointsToExclude = [
                                            "defender_bonus",
                                            "spielmacher_bonus",
                                            "save_bonus",
                                            "interceptions_bonus",
                                        ];
                                        let bonusPoints = 0;
                                        const breakdown = score.breakdown || {};

                                        for (const bonus of bonusPointsToExclude) {
                                            if (breakdown[bonus]) {
                                                bonusPoints += breakdown[bonus].points;
                                            }
                                        }
                                        totalPoints += score.totalPoints || 0;
                                        totalBonusPoints += bonusPoints;
                                    });
                                    const basePoints = totalPoints - totalBonusPoints;
                                    homeScore += (basePoints * 1.5) + totalBonusPoints;
                                } else {
                                    const totalPlayerScore = scoresArray.reduce((sum, score) => sum + (score.totalPoints || 0), 0);
                                    homeScore += totalPlayerScore;
                                }
                            });

                            awayTeamPlayers.forEach(playerId => {
                                const scoresArray = playerScoresForRound.get(playerId) || [];
                                const isCaptain = playerId === String(awaySelection.captain);

                                if (isCaptain) {
                                    let totalPoints = 0;
                                    let totalBonusPoints = 0;
                                    scoresArray.forEach(score => {
                                        const bonusPointsToExclude = [
                                            "defender_bonus",
                                            "spielmacher_bonus",
                                            "save_bonus",
                                            "interceptions_bonus",
                                        ];
                                        let bonusPoints = 0;
                                        const breakdown = score.breakdown || {};

                                        for (const bonus of bonusPointsToExclude) {
                                            if (breakdown[bonus]) {
                                                bonusPoints += breakdown[bonus].points;
                                            }
                                        }
                                        totalPoints += score.totalPoints || 0;
                                        totalBonusPoints += bonusPoints;
                                    });
                                    const basePoints = totalPoints - totalBonusPoints;
                                    awayScore += (basePoints * 1.5) + totalBonusPoints;
                                } else {
                                    const totalPlayerScore = scoresArray.reduce((sum, score) => sum + (score.totalPoints || 0), 0);
                                    awayScore += totalPlayerScore;
                                }
                            });
                        }
                    }
                    newScores[matchId] = { homeScore, awayScore };
                }
            }
            setMatchScores(newScores);

            // Helper to check if a round is a tussenronde knockout round
            const isCaptainOnlyKnockoutRoundData = (roundData) => {
                if (roundData.type !== 'knockout') return false;
                return roundData.stageName === 'Knockout Round Play-offs' || ['Quarter-finals', 'Semi-finals', 'Final'].includes(roundData.stageName);
            };

            // Calculate captain-only knockout round scores (tussenronde + finaleronde)
            const intermediateRoundKeys = Object.entries(scheduleData)
                .filter(([roundKey, roundData]) => isCaptainOnlyKnockoutRoundData(roundData) || roundKey.startsWith('intermediateRound'))
                .map(([roundKey]) => roundKey);

            // Fetch player scores for intermediate rounds that are running/open
            // Voor finaleronde rounds is roundId null; scores staan opgeslagen onder het roundKey (stageId-leg)
            const intermediateScorePromises = intermediateRoundKeys
                .filter(roundKey => (scheduleData[roundKey]?.roundId || roundKey) && scheduleData[roundKey]?.status !== 'closed')
                .map(roundKey => {
                    const roundData = scheduleData[roundKey];
                    const storageId = roundData.roundId || roundKey;
                    const scoresQuery = collection(db, `playerScores/${auction.season}/rounds/${storageId}/scores`);
                    return getDocs(scoresQuery).then(snapshot => {
                        const scoresByPlayer = new Map();
                        snapshot.forEach(doc => {
                            const scoreData = doc.data();
                            const playerId = String(scoreData.playerId);
                            if (!scoresByPlayer.has(playerId)) {
                                scoresByPlayer.set(playerId, []);
                            }
                            scoresByPlayer.get(playerId).push(scoreData);
                        });
                        return { roundKey, roundId: roundData.roundId, scores: scoresByPlayer };
                    });
                });

            const intermediatePlayerScores = await Promise.all(intermediateScorePromises);
            const intermediateScoresMap = new Map();
            intermediatePlayerScores.forEach(({ roundKey, scores }) => {
                intermediateScoresMap.set(roundKey, scores);
            });

            const intermediateRankingPromises = intermediateRoundKeys.map(roundKey => {
                const roundRankingRef = doc(db, 'auctions', auctionId, 'roundRankings', roundKey);
                return getDoc(roundRankingRef).then(roundRankingSnap => {
                    if (roundRankingSnap.exists()) {
                        return { roundKey, results: roundRankingSnap.data().results || [] };
                    }
                    return { roundKey, results: [] };
                });
            });

            const intermediateRankingsResults = await Promise.all(intermediateRankingPromises);
            const newIntermediateScores = {};
            
            for (const { roundKey, results } of intermediateRankingsResults) {
                const roundData = scheduleData[roundKey];
                if (!roundData) continue;

                const selectionId = `${roundKey}-captain`;
                newIntermediateScores[roundKey] = {};

                // Initialize all participants with 0 score
                participants.forEach(participant => {
                    newIntermediateScores[roundKey][participant.id] = 0;
                });

                // For closed rounds, use roundRankings
                if (roundData.status === 'closed' && results.length > 0) {
                    results.forEach(result => {
                        newIntermediateScores[roundKey][result.uid] = result.playerPoints || 0;
                    });
                } else if (roundData.roundId || roundKey) {
                    // For running/open rounds, calculate from playerScores
                    const playerScoresForRound = intermediateScoresMap.get(roundKey);
                    if (playerScoresForRound) {
                        participants.forEach(participant => {
                            const teamPlayers = Array.from(teamCompositions.get(participant.id) || []);
                            const participantSelection = allSelections[participant.id]?.[selectionId] || {};
                            let totalScore = 0;

                            teamPlayers.forEach(playerId => {
                                const scoresArray = playerScoresForRound.get(playerId) || [];
                                const isCaptain = playerId === String(participantSelection.captain);

                                if (isCaptain) {
                                    let totalPoints = 0;
                                    let totalBonusPoints = 0;
                                    scoresArray.forEach(score => {
                                        const bonusPointsToExclude = [
                                            "defender_bonus",
                                            "spielmacher_bonus",
                                            "save_bonus",
                                            "interceptions_bonus",
                                        ];
                                        let bonusPoints = 0;
                                        const breakdown = score.breakdown || {};

                                        for (const bonus of bonusPointsToExclude) {
                                            if (breakdown[bonus]) {
                                                bonusPoints += breakdown[bonus].points;
                                            }
                                        }
                                        totalPoints += score.totalPoints || 0;
                                        totalBonusPoints += bonusPoints;
                                    });
                                    const basePoints = totalPoints - totalBonusPoints;
                                    totalScore += (basePoints * 1.5) + totalBonusPoints;
                                } else {
                                    const playerScore = scoresArray.reduce((sum, score) => sum + (score.totalPoints || 0), 0);
                                    totalScore += playerScore;
                                }
                            });

                            newIntermediateScores[roundKey][participant.id] = totalScore;
                        });
                    }
                }
            }

            setIntermediateRoundScores(newIntermediateScores);
        };

        calculateAllScores();
    }, [scheduleData, auction, participants, allSelections, auctionId, participantsByName]);

    // Persist showOnlyMyMatches to localStorage
    useEffect(() => {
        if (auctionId) {
            localStorage.setItem(`showOnlyMyMatches_${auctionId}`, JSON.stringify(showOnlyMyMatches));
        }
    }, [showOnlyMyMatches, auctionId]);

    // Persist showTacticsAndCaptains to localStorage
    useEffect(() => {
        if (auctionId) {
            localStorage.setItem(`showTacticsAndCaptains_${auctionId}`, JSON.stringify(showTacticsAndCaptains));
        }
    }, [showTacticsAndCaptains, auctionId]);

    const handleOpenTacticModal = (match, matchId) => {
        setEditingMatch({ match, matchId });
        setIsModalOpen(true);
    };

    const handleCloseTacticModal = () => {
        setIsModalOpen(false);
        setEditingMatch(null);
    };

    // Handlers for intermediate round captain selection
    const handleOpenIntermediateCaptainModal = (roundKey, roundData) => {
        setEditingIntermediateRound({ roundKey, roundData });
        setIsIntermediateCaptainModalOpen(true);
    };

    const handleCloseIntermediateCaptainModal = () => {
        setIsIntermediateCaptainModalOpen(false);
        setEditingIntermediateRound(null);
    };

    const handleSaveIntermediateCaptain = async (captain) => {
        if (!editingIntermediateRound || !user || !auctionId) return;

        setError(null);
        const { roundKey } = editingIntermediateRound;
        const selectionId = `${roundKey}-captain`;

        const updatedSelections = {
            ...selections,
            [selectionId]: { captain },
        };

        setSelections(updatedSelections);
        handleCloseIntermediateCaptainModal();

        try {
            const selectionRef = doc(db, 'auctions', auctionId, 'matchSelections', user.uid);
            await setDoc(selectionRef, { selections: updatedSelections }, { merge: true });
        } catch (err) {
            setError('Fout bij het opslaan van je aanvoerder.');
            console.error(err);
            setSelections(selections);
        }
    };

    const handleParticipantClick = (participantName, currentRoundId, matchId, roundStatus) => {
        console.log('🔍 Schedule.jsx - handleParticipantClick called with:', {
            participantName,
            currentRoundId,
            matchId,
            auction: auction?.season,
            roundStatus,
        });

        const participant = participantsByName.get(participantName);
        if (participant) {
            console.log('🔍 Schedule.jsx - Setting participant and roundId:', {
                participant: participant.name,
                modalRoundId: currentRoundId,
                modalMatchId: matchId,
                seasonId: auction?.season,
                roundStatus,
            });
            setSelectedParticipant(participant);
            setModalRoundId(currentRoundId); // Set the round ID for the modal
            setModalMatchId(matchId);
            setModalRoundStatus(roundStatus);
        } else {
            console.log('❌ Schedule.jsx - Participant not found:', participantName);
        }
    };

    const handleCloseParticipantModal = () => {
        setSelectedParticipant(null);
        setModalRoundId(null);
        setModalMatchId(null);
        setModalRoundStatus(null);
    };

    const handleSaveTactic = async (selection) => {
        if (!editingMatch || !user || !auctionId) return;

        if (selection.tactic === 'catenaccio' && selection.captain) {
            const captainPlayer = teamPlayers.find(p => p.id === Number(selection.captain));
            if (captainPlayer && captainPlayer.position === 'Keeper') {
                setInfoDialogMessage('Het is niet toegestaan om met de Catenaccio-tactiek een keeper als aanvoerder te kiezen.');
                setInfoDialogOpen(true);
                return;
            }
        }

        setError(null);
        const { matchId } = editingMatch;

        const updatedSelections = {
            ...selections,
            [matchId]: selection,
        };

        setSelections(updatedSelections);
        handleCloseTacticModal();

        try {
            const selectionRef = doc(db, 'auctions', auctionId, 'matchSelections', user.uid);
            await setDoc(selectionRef, { selections: updatedSelections }, { merge: true });
        } catch (err) {
            setError('Fout bij het opslaan van je keuzes.');
            console.error(err);
            setSelections(selections);
        }
    };

    if (loading) {
        return <Box sx={page.loadingContainer}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Wedstrijden
            </Typography>
            
            {/* Global filters - always visible at top */}
            <Box sx={{
                mb: 2,
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 0, sm: 3 },
                alignItems: { xs: 'flex-start', sm: 'center' }
            }}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={showOnlyMyMatches}
                            onChange={(e) => setShowOnlyMyMatches(e.target.checked)}
                        />
                    }
                    label="Toon alleen mijn wedstrijden"
                    sx={{
                        mb: { xs: 1, sm: 0 }
                    }}
                />
                <FormControlLabel
                    control={
                        <Switch
                            checked={showTacticsAndCaptains}
                            onChange={(e) => setShowTacticsAndCaptains(e.target.checked)}
                        />
                    }
                    label="Tactiek en aanvoerders tonen"
                />
            </Box>

            {/* Finaleronde sectie - captain only, geen tactiekpunten */}
            {finalRounds.length > 0 && finalRoundParticipantsList.length > 0 && (
                <>
                    <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
                        Finaleronde
                    </Typography>
                    {finalRounds.map(([roundKey, roundData], index) => {
                        const selectionId = `${roundKey}-captain`;
                        const displayParticipants = showOnlyMyMatches
                            ? finalRoundParticipantsList.filter(p => p.uid === user?.uid)
                            : finalRoundParticipantsList;

                        if (displayParticipants.length === 0) return null;

                        const roundLabel = roundData.stageName === 'Final'
                            ? 'Final'
                            : `${roundData.stageName} ${roundData.leg || ''}`.trim();

                        return (
                            <Accordion key={roundKey} defaultExpanded={index === 0} sx={{ mb: 2, backgroundImage: 'none' }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`final-panel${index}-content`}
                                    id={`final-panel${index}-header`}
                                >
                                    <Typography variant="h6">{roundLabel}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {displayParticipants.map((participant) => {
                                            const isCurrentUser = participant.uid === user?.uid;
                                            const participantSelection = allSelections[participant.uid]?.[selectionId] || {};
                                            const captainChosen = !!participantSelection.captain;
                                            const captainName = participantSelection.captain
                                                ? (playerDetails.get(String(participantSelection.captain))?.name || 'Onbekend')
                                                : 'Niet gekozen';

                                            return (
                                                <Box
                                                    key={`${roundKey}-${participant.uid}`}
                                                    sx={{
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '8px',
                                                        p: 1.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            onClick={() => handleParticipantClick(participant.name, roundKey, selectionId, roundData.status)}
                                                            sx={{ cursor: 'pointer', fontWeight: isCurrentUser ? 'bold' : 'normal', fontSize: '0.875rem' }}
                                                        >
                                                            {participant.name}
                                                        </Typography>

                                                        {!showTacticsAndCaptains && isCurrentUser && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                <Tooltip title="Selecteer aanvoerder">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={roundData.status !== 'open' ? undefined : () => handleOpenIntermediateCaptainModal(roundKey, roundData)}
                                                                        disabled={roundData.status !== 'open'}
                                                                        sx={{ p: 0.5 }}
                                                                    >
                                                                        <StarIcon fontSize="small" sx={{ color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}

                                                        {showTacticsAndCaptains && isCurrentUser && (
                                                            <Box
                                                                onClick={roundData.status !== 'open' ? undefined : () => handleOpenIntermediateCaptainModal(roundKey, roundData)}
                                                                sx={{
                                                                    cursor: roundData.status === 'open' ? 'pointer' : 'default',
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'flex', alignItems: 'center' }}
                                                                >
                                                                    <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                    {captainName}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {showTacticsAndCaptains && !isCurrentUser && (roundData.status === 'running' || roundData.status === 'closed') && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{ color: 'text.secondary', fontSize: '0.75rem', display: 'flex', alignItems: 'center', mt: 0.5 }}
                                                            >
                                                                <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                {captainName}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '50px', pl: 1, borderLeft: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                                        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                            {intermediateRoundScores[roundKey]?.[participant.uid] !== undefined
                                                                ? intermediateRoundScores[roundKey][participant.uid]
                                                                : '-'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </>
            )}

            {/* Knock-out sectie - alleen zichtbaar als er achtste finale rondes zijn */}
            {knockoutAchtsteRounds.length > 0 && (
                <>
                    <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
                        Knock-out — Achtste finales
                    </Typography>
                    {knockoutAchtsteRounds.map(([roundKey, roundData], index) => {
                        const matches = roundData.matches || [];
                        const filteredMatches = showOnlyMyMatches
                            ? matches.filter(match => match.home === currentUserParticipantName || match.away === currentUserParticipantName)
                            : matches;

                        if (filteredMatches.length === 0) return null;

                        const legNumber = roundData.leg ? roundData.leg.split('/')[0] : index + 1;

                        return (
                            <Accordion key={roundKey} defaultExpanded={roundData.status !== 'closed'} sx={{ mb: 2, backgroundImage: 'none' }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`knockout-panel${index}-content`}
                                    id={`knockout-panel${index}-header`}
                                >
                                    <Typography variant="h6">Speelronde {legNumber}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {filteredMatches.map((match, filteredIndex) => {
                                            const originalIndex = matches.findIndex(m => m.home === match.home && m.away === match.away);
                                            const matchId = `${roundKey}-${originalIndex}`;

                                            const KnockoutTeamDisplay = ({ teamName }) => {
                                                const teamUid = participantNameToUidMap.get(teamName);
                                                const teamSelection = allSelections[teamUid]?.[matchId] || {};

                                                const captainChosen = !!teamSelection.captain;
                                                const tacticChosen = !!teamSelection.tactic;

                                                const captainName = teamSelection.captain ?
                                                    (playerDetails.get(String(teamSelection.captain))?.name || 'Onbekend') :
                                                    'Niet gekozen';

                                                const tacticName = teamSelection.tactic ?
                                                    (tactics.find(t => t.id === teamSelection.tactic)?.name || 'Onbekend') :
                                                    'Niet gekozen';

                                                return (
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        py: 0.5,
                                                        px: 1,
                                                        borderRadius: 1,
                                                        border: '1px solid transparent'
                                                    }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography
                                                                    variant="body1"
                                                                    onClick={() => {
                                                                        handleParticipantClick(teamName, roundData.roundId, matchId, roundData.status);
                                                                    }}
                                                                    sx={{
                                                                        cursor: 'pointer',
                                                                        fontWeight: teamName === currentUserParticipantName ? 'bold' : 'normal',
                                                                        fontSize: '0.875rem'
                                                                    }}
                                                                >
                                                                    {teamName}
                                                                </Typography>

                                                                {/* Eigen team - iconen voor aanvoerder/tactiek kiezen */}
                                                                {!showTacticsAndCaptains && teamName === currentUserParticipantName && (
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                        <Tooltip title="Selecteer aanvoerder/tactiek">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={roundData.status !== 'open' ? undefined : () => handleOpenTacticModal(match, matchId)}
                                                                                disabled={roundData.status !== 'open'}
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <StarIcon
                                                                                    fontSize="small"
                                                                                    sx={{ color: captainChosen ? 'success.main' : 'error.main' }}
                                                                                />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                        <Tooltip title="Selecteer aanvoerder/tactiek">
                                                                            <IconButton
                                                                                size="small"
                                                                                onClick={roundData.status !== 'open' ? undefined : () => handleOpenTacticModal(match, matchId)}
                                                                                disabled={roundData.status !== 'open'}
                                                                                sx={{ p: 0.5 }}
                                                                            >
                                                                                <PsychologyIcon
                                                                                    fontSize="small"
                                                                                    sx={{ color: tacticChosen ? 'success.main' : 'error.main' }}
                                                                                />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                    </Box>
                                                                )}
                                                            </Box>

                                                            {/* Eigen team - toon captain + tactiek als toggle aan */}
                                                            {showTacticsAndCaptains && teamName === currentUserParticipantName && (
                                                                <Box
                                                                    onClick={roundData.status !== 'open' ? undefined : () => handleOpenTacticModal(match, matchId)}
                                                                    sx={{
                                                                        cursor: roundData.status === 'open' ? 'pointer' : 'default',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.75rem',
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}
                                                                    >
                                                                        <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                        {captainName}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.75rem',
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}
                                                                    >
                                                                        <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                        {tacticName}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {/* Andere teams - toon captain + tactiek alleen als toggle aan en ronde running/closed */}
                                                            {showTacticsAndCaptains && teamName !== currentUserParticipantName && (roundData.status === 'running' || roundData.status === 'closed') && (
                                                                <>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.75rem',
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}
                                                                    >
                                                                        <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                        {captainName}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.75rem',
                                                                            display: 'flex',
                                                                            alignItems: 'center'
                                                                        }}
                                                                    >
                                                                        <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                        {tacticName}
                                                                    </Typography>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            };

                                            return (
                                                <Box
                                                    key={matchId}
                                                    sx={{
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box sx={{ flex: 1, p: 1.5 }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <KnockoutTeamDisplay teamName={match.home} />
                                                                <KnockoutTeamDisplay teamName={match.away} />
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '60px',
                                                            py: 1.5,
                                                            px: 1,
                                                            borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                        }}>
                                                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                                {matchScores[matchId]?.homeScore ?? '-'}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                                {matchScores[matchId]?.awayScore ?? '-'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}

                    {/* Totaal overzicht */}
                    {knockoutAchtsteRounds.length >= 2 && (
                        <Accordion defaultExpanded={knockoutAchtsteRounds.every(([, rd]) => rd.status === 'closed')} sx={{ mb: 2, backgroundImage: 'none' }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="knockout-totaal-content"
                                id="knockout-totaal-header"
                            >
                                <Typography variant="h6">Totaal</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {(() => {
                                        const round1 = knockoutAchtsteRounds[0];
                                        const round2 = knockoutAchtsteRounds[1];
                                        const matches1 = round1?.[1]?.matches || [];

                                        const allMatches = showOnlyMyMatches
                                            ? matches1.filter(match => match.home === currentUserParticipantName || match.away === currentUserParticipantName)
                                            : matches1;

                                        return allMatches.map((match, idx) => {
                                            const matchIndex = matches1.findIndex(m => m.home === match.home && m.away === match.away);
                                            const matchId1 = `${round1[0]}-${matchIndex}`;
                                            const matchId2 = `${round2[0]}-${matchIndex}`;

                                            const homeScore1 = matchScores[matchId1]?.homeScore || 0;
                                            const awayScore1 = matchScores[matchId1]?.awayScore || 0;
                                            const homeScore2 = matchScores[matchId2]?.homeScore || 0;
                                            const awayScore2 = matchScores[matchId2]?.awayScore || 0;

                                            const totalHome = homeScore1 + homeScore2;
                                            const totalAway = awayScore1 + awayScore2;

                                            let homeWins = false;
                                            let awayWins = false;
                                            let tiebreaker = '';

                                            if (totalHome > totalAway) {
                                                homeWins = true;
                                            } else if (totalAway > totalHome) {
                                                awayWins = true;
                                            } else {
                                                // Tiebreaker: wie meer scoorde in ronde 2
                                                if (homeScore2 > awayScore2) {
                                                    homeWins = true;
                                                    tiebreaker = '(ronde 2)';
                                                } else if (awayScore2 > homeScore2) {
                                                    awayWins = true;
                                                    tiebreaker = '(ronde 2)';
                                                } else {
                                                    tiebreaker = '(gelijk)';
                                                }
                                            }

                                            return (
                                                <Box
                                                    key={`totaal-${idx}`}
                                                    sx={{
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden',
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box sx={{ flex: 1, p: 1.5 }}>
                                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                                <Box sx={{ py: 0.5, px: 1 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            fontWeight: homeWins ? 'bold' : 'normal',
                                                                            fontSize: '0.875rem',
                                                                            color: homeWins ? 'success.main' : 'text.primary'
                                                                        }}
                                                                    >
                                                                        {match.home} {homeWins && tiebreaker ? tiebreaker : ''}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ py: 0.5, px: 1 }}>
                                                                    <Typography
                                                                        variant="body1"
                                                                        sx={{
                                                                            fontWeight: awayWins ? 'bold' : 'normal',
                                                                            fontSize: '0.875rem',
                                                                            color: awayWins ? 'success.main' : 'text.primary'
                                                                        }}
                                                                    >
                                                                        {match.away} {awayWins && tiebreaker ? tiebreaker : ''}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            minWidth: '60px',
                                                            py: 1.5,
                                                            px: 1,
                                                            borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                        }}>
                                                            <Typography sx={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: 'bold',
                                                                color: homeWins ? 'success.main' : 'text.primary'
                                                            }}>
                                                                {totalHome}
                                                            </Typography>
                                                            <Typography sx={{
                                                                fontSize: '1.1rem',
                                                                fontWeight: 'bold',
                                                                color: awayWins ? 'success.main' : 'text.primary'
                                                            }}>
                                                                {totalAway}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            );
                                        });
                                    })()}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                </>
            )}

            {/* Tussenronde sectie - alleen zichtbaar als er intermediate rounds zijn */}
            {intermediateRounds.length > 0 && (
                <>
                    <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
                        Tussenronde
                    </Typography>
                    {intermediateRounds.map(([roundKey, roundData], index) => {
                        // Use leg from data, fallback to old naming format for backward compatibility
                        const roundNumber = roundData.leg ? roundData.leg.split('/')[0] : roundKey.replace('intermediateRound', '');
                        const selectionId = `${roundKey}-captain`;
                        
                        // Filter participants based on showOnlyMyMatches
                        const displayParticipants = showOnlyMyMatches 
                            ? intermediateParticipants.filter(p => p.uid === user?.uid)
                            : intermediateParticipants;

                        if (displayParticipants.length === 0) return null;

                        return (
                            <Accordion key={roundKey} defaultExpanded={index === 0} sx={{ mb: 2, backgroundImage: 'none' }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    aria-controls={`intermediate-panel${index}-content`}
                                    id={`intermediate-panel${index}-header`}
                                >
                                    <Typography variant="h6">Tussenronde {roundNumber}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {displayParticipants.map((participant) => {
                                            const isCurrentUser = participant.uid === user?.uid;
                                            const participantSelection = allSelections[participant.uid]?.[selectionId] || {};
                                            const captainChosen = !!participantSelection.captain;
                                            const captainName = participantSelection.captain 
                                                ? (playerDetails.get(String(participantSelection.captain))?.name || 'Onbekend')
                                                : 'Niet gekozen';

                                            return (
                                                <Box
                                                    key={participant.uid}
                                                    sx={{
                                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                                        borderRadius: '8px',
                                                        p: 1.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                                        }
                                                    }}
                                                >
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            onClick={() => handleParticipantClick(participant.name, roundKey, selectionId, roundData.status)}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                fontWeight: isCurrentUser ? 'bold' : 'normal',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            {participant.name}
                                                        </Typography>

                                                        {/* Eigen team - icoon voor aanvoerder kiezen */}
                                                        {!showTacticsAndCaptains && isCurrentUser && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                                                <Tooltip title="Selecteer aanvoerder">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={roundData.status !== 'open' ? undefined : () => handleOpenIntermediateCaptainModal(roundKey, roundData)}
                                                                        disabled={roundData.status !== 'open'}
                                                                        sx={{ p: 0.5 }}
                                                                    >
                                                                        <StarIcon
                                                                            fontSize="small"
                                                                            sx={{ color: captainChosen ? 'success.main' : 'error.main' }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}

                                                        {/* Eigen team - toon aanvoerder wanneer toggle aan */}
                                                        {showTacticsAndCaptains && isCurrentUser && (
                                                            <Box
                                                                onClick={roundData.status !== 'open' ? undefined : () => handleOpenIntermediateCaptainModal(roundKey, roundData)}
                                                                sx={{
                                                                    cursor: roundData.status === 'open' ? 'pointer' : 'default',
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'text.secondary',
                                                                        fontSize: '0.75rem',
                                                                        display: 'flex',
                                                                        alignItems: 'center'
                                                                    }}
                                                                >
                                                                    <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                    {captainName}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {/* Andere teams - toon aanvoerder alleen als toggle aan en ronde running/closed */}
                                                        {showTacticsAndCaptains && !isCurrentUser && (roundData.status === 'running' || roundData.status === 'closed') && (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    mt: 0.5
                                                                }}
                                                            >
                                                                <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                {captainName}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {/* Score kolom */}
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: '50px',
                                                        pl: 1,
                                                        borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                    }}>
                                                        <Typography sx={{ fontSize: '1rem', fontWeight: 'bold' }}>
                                                            {intermediateRoundScores[roundKey]?.[participant.uid] !== undefined 
                                                                ? intermediateRoundScores[roundKey][participant.uid]
                                                                : '-'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </>
            )}

            {/* Poulefase sectie */}
            <Typography variant="h5" gutterBottom sx={{ mt: (intermediateRounds.length > 0 || finalRounds.length > 0) ? 3 : 0 }}>
                Poulefase
            </Typography>
            {sortedSchedule.map(([roundKey, roundData], index) => {
                const matches = roundData.matches || [];
                const filteredMatches = showOnlyMyMatches
                    ? matches.filter(match => match.home === currentUserParticipantName || match.away === currentUserParticipantName)
                    : matches;

                if (filteredMatches.length === 0) return null;

                return (
                    <Accordion key={roundKey} defaultExpanded={index === firstNonClosedRoundIndex} sx={{ mb: 2, backgroundImage: 'none' }}>
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`panel${index}-content`}
                            id={`panel${index}-header`}
                        >
                            <Typography variant="h6">Speelronde {roundKey.replace('round', '')}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {filteredMatches.map((match, filteredIndex) => {
                                    // Find the original index of this match in the unfiltered matches array
                                    const originalIndex = matches.findIndex(m => m.home === match.home && m.away === match.away);
                                    const matchId = `${roundKey}-${originalIndex}`;

                                    const TeamDisplay = ({ teamName, roundId }) => {
                                        const teamUid = participantNameToUidMap.get(teamName);
                                        const teamSelection = allSelections[teamUid]?.[matchId] || {};

                                        const captainChosen = !!teamSelection.captain;
                                        const tacticChosen = !!teamSelection.tactic;

                                        const captainName = teamSelection.captain ?
                                            (playerDetails.get(String(teamSelection.captain))?.name || 'Onbekend') :
                                            'Niet gekozen';

                                        const tacticName = teamSelection.tactic ?
                                            (tactics.find(t => t.id === teamSelection.tactic)?.name || 'Onbekend') :
                                            'Niet gekozen';

                                        return (
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                py: 0.5,
                                                px: 1,
                                                borderRadius: 1,
                                                border: '1px solid transparent'
                                            }}>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography
                                                            variant="body1"
                                                            onClick={() => {
                                                                handleParticipantClick(teamName, roundId, matchId, roundData.status);
                                                            }}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                fontWeight: teamName === currentUserParticipantName ? 'bold' : 'normal',
                                                                fontSize: '0.875rem'
                                                            }}
                                                        >
                                                            {teamName}
                                                        </Typography>

                                                        {/* Eigen team - alleen iconen tonen wanneer schuivertje uit staat */}
                                                        {!showTacticsAndCaptains && teamName === currentUserParticipantName && (
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <Tooltip title="Selecteer aanvoerder/tactiek">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={roundData.status !== 'open' ? undefined : () => handleOpenTacticModal(match, matchId)}
                                                                        disabled={roundData.status !== 'open'}
                                                                        sx={{ p: 0.5 }}
                                                                    >
                                                                        <StarIcon
                                                                            fontSize="small"
                                                                            sx={{ color: captainChosen ? 'success.main' : 'error.main' }}
                                                                        />
                                                                        <PsychologyIcon
                                                                            fontSize="small"
                                                                            sx={{ color: tacticChosen ? 'success.main' : 'error.main', ml: 0.5 }}
                                                                        />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    {/* Eigen team - altijd tonen wanneer schuivertje aan staat */}
                                                    {showTacticsAndCaptains && teamName === currentUserParticipantName && (
                                                        <Box
                                                            onClick={roundData.status !== 'open' ? undefined : () => handleOpenTacticModal(match, matchId)}
                                                            sx={{
                                                                cursor: roundData.status === 'open' ? 'pointer' : 'default',
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                {captainName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                {tacticName}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {/* Andere teams - alleen tonen als schuivertje aan staat EN ronde status running of closed */}
                                                    {showTacticsAndCaptains && teamName !== currentUserParticipantName && (roundData.status === 'running' || roundData.status === 'closed') && (
                                                        <>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <StarIcon fontSize="inherit" sx={{ mr: 0.5, color: captainChosen ? 'success.main' : 'error.main' }} />
                                                                {captainName}
                                                            </Typography>
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    color: 'text.secondary',
                                                                    fontSize: '0.75rem',
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <PsychologyIcon fontSize="inherit" sx={{ mr: 0.5, color: tacticChosen ? 'success.main' : 'error.main' }} />
                                                                {tacticName}
                                                            </Typography>
                                                        </>
                                                    )}
                                                </Box>
                                            </Box>
                                        );
                                    };

                                    return (
                                        <Box
                                            key={matchId}
                                            sx={{
                                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                                borderRadius: '8px',
                                                overflow: 'hidden',
                                                transition: 'all 0.2s ease-in-out',
                                                '&:hover': {
                                                    borderColor: 'rgba(255, 255, 255, 0.2)'
                                                }
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Box sx={{ flex: 1, p: 1.5 }}>
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                        <TeamDisplay teamName={match.home} roundId={roundData.roundId} />
                                                        <TeamDisplay teamName={match.away} roundId={roundData.roundId} />
                                                    </Box>
                                                </Box>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    minWidth: '60px',
                                                    py: 1.5,
                                                    px: 1,
                                                    borderLeft: '1px solid rgba(255, 255, 255, 0.1)'
                                                }}>
                                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                        {matchScores[matchId]?.homeScore ?? '-'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>
                                                        {matchScores[matchId]?.awayScore ?? '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
            {editingMatch && (
                <TacticsModal
                    open={isModalOpen}
                    onClose={handleCloseTacticModal}
                    onSave={handleSaveTactic}
                    match={editingMatch.match}
                    teamPlayers={teamPlayers}
                    tactics={tactics}
                    initialSelection={selections[editingMatch.matchId] || {}}
                />
            )}
            {/* Modal voor tussenronde aanvoerder selectie (alleen captain, geen tactiek) */}
            {editingIntermediateRound && (
                <Dialog open={isIntermediateCaptainModalOpen} onClose={handleCloseIntermediateCaptainModal} fullWidth maxWidth="xs">
                    <DialogTitle>
                        Aanvoerder kiezen
                        <Typography variant="body2" color="text.secondary">
                            {editingIntermediateRound.roundData?.stageName === 'Knockout Round Play-offs'
                                ? `Tussenronde ${editingIntermediateRound.roundData.leg ? editingIntermediateRound.roundData.leg.split('/')[0] : editingIntermediateRound.roundKey.replace('intermediateRound', '')}`
                                : editingIntermediateRound.roundData?.stageName === 'Final'
                                    ? 'Final'
                                    : `${editingIntermediateRound.roundData?.stageName || 'Finaleronde'} ${editingIntermediateRound.roundData?.leg || ''}`.trim()}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel id="intermediate-captain-select-label">Aanvoerder (1.5x punten)</InputLabel>
                                <Select
                                    labelId="intermediate-captain-select-label"
                                    value={selections[`${editingIntermediateRound.roundKey}-captain`]?.captain || ''}
                                    label="Aanvoerder (1.5x punten)"
                                    onChange={(e) => handleSaveIntermediateCaptain(e.target.value)}
                                >
                                    {teamPlayers.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseIntermediateCaptainModal}>Sluiten</Button>
                    </DialogActions>
                </Dialog>
            )}
            {selectedParticipant && (
                <ParticipantTeamModal
                    open={!!selectedParticipant}
                    onClose={handleCloseParticipantModal}
                    auctionId={auctionId}
                    participant={selectedParticipant}
                    roundId={modalRoundId}
                    seasonId={auction?.season}
                    matchId={modalMatchId}
                    roundStatus={modalRoundStatus}
                />
            )}
            <Dialog open={infoDialogOpen} onClose={() => setInfoDialogOpen(false)}>
                <DialogTitle>Ongeldige combinatie</DialogTitle>
                <DialogContent>
                    <DialogContentText>{infoDialogMessage}</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInfoDialogOpen(false)}>Sluiten</Button>
                </DialogActions>
            </Dialog>
            {/* Debug logging for modal props */}
            {selectedParticipant && console.log('🔍 Schedule.jsx - Rendering ParticipantTeamModal with props:', {
                open: !!selectedParticipant,
                auctionId,
                participant: selectedParticipant?.name,
                roundId: modalRoundId,
                seasonId: auction?.season
            })}
        </Box>
    );
}

export default Schedule;
